import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import PageLayout from "../components/PageLayout";
import { apiUrl } from "../services/api";
import { t } from "../i18n";
import { IconCheck, IconPencil, IconX } from "@tabler/icons-react";

type MetadataTab = "units" | "weapons" | "airdromes";

type BaseMetadata = {
  id: number;
  event_count: number;
  created_at: string | null;
  updated_at: string | null;
};

type UnitMetadata = BaseMetadata & {
  unit_name: string;
  family?: string | null;
  domain?: string | null;
  category?: string | null;
  role?: string | null;
};

type WeaponMetadata = BaseMetadata & {
  weapon_name: string;
  family?: string | null;
  category?: string | null;
  guidance?: string | null;
  role?: string | null;
};

type AirdromeMetadata = BaseMetadata & {
  airdrome_name: string;
  airdrome_type?: string | null;
};

type MetadataItem = (UnitMetadata | WeaponMetadata | AirdromeMetadata) & {
  backfill_required?: boolean;
  backfill_context?: {
    tag_keys: string[];
    from: string;
    to: string;
  };
};

type BackfillPreview = {
  event_count: number;
  metrics: {
    changed: number;
    added: number;
    removed: number;
    delta: {
      increased: number;
      decreased: number;
    };
    swap?: Array<{
      schema_name: string;
      tag_key: string;
      from_value: string;
      to_value: string;
      from_before_count: number;
      from_after_count: number;
      to_before_count: number;
      to_after_count: number;
      from_delta: number;
      to_delta: number;
    }>;
  };
  proficiencies: {
    affected: number;
    items?: Array<{
      id: string;
      display_name: string;
      proficiency_type: string;
      metric: string;
    }>;
  };
  pilots_affected?: number;
};

type BackfillResult = {
  preview: BackfillPreview;
  result: {
    event_count: number;
    metrics: {
      changed: number;
      added: number;
      removed: number;
      delta: {
        increased: number;
        decreased: number;
      };
      proficiencies?: number;
    };
  };
};

type SortDirection = "asc" | "desc";
type SortKey =
  | "unit_name"
  | "family"
  | "domain"
  | "category"
  | "role"
  | "weapon_name"
  | "guidance"
  | "airdrome_name"
  | "airdrome_type"
  | "event_count"
  | "updated_at";

const TOKEN_STORAGE_KEY = "checkride.admin.jwt";

const tabs: { key: MetadataTab; label: string }[] = [
  { key: "units", label: "Units" },
  { key: "weapons", label: "Weapons" },
  { key: "airdromes", label: "Airdromes" }
];

const AdminMetadataPage = () => {
  const [tab, setTab] = useState<MetadataTab>("units");
  const [items, setItems] = useState<MetadataItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("unit_name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [backfillTarget, setBackfillTarget] = useState<MetadataItem | null>(null);
  const [backfillPreview, setBackfillPreview] = useState<BackfillPreview | null>(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillError, setBackfillError] = useState<string | null>(null);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResult, setBackfillResult] = useState<BackfillResult | null>(null);
  const [backfillSnapshot, setBackfillSnapshot] = useState<BackfillPreview | null>(null);
  const [backfillRunError, setBackfillRunError] = useState<string | null>(null);
  const [backfillContextById, setBackfillContextById] = useState<
    Record<number, MetadataItem["backfill_context"]>
  >({});
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const authHeader = useMemo(() => localStorage.getItem(TOKEN_STORAGE_KEY), []);

  const handleAuthError = (message?: string | null) => {
    if (!message) return false;
    if (!message.toLowerCase().includes("signature expired")) return false;
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    window.location.hash = "#/admin/sign_in";
    return true;
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(apiUrl(`/metadata/${tab}`).toString(), {
          headers: {
            Accept: "application/json",
            ...(authHeader ? { Authorization: authHeader } : {})
          }
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          const message = payload?.error || payload?.message || "Failed to load metadata";
          if (handleAuthError(message)) return;
          throw new Error(message);
        }

        const payload = await response.json();
        if (mounted) {
          setItems(Array.isArray(payload?.items) ? payload.items : []);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load metadata";
        if (handleAuthError(message)) return;
        if (mounted) {
          setError(message);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [tab, authHeader]);

  const backfillContext = useMemo(() => {
    if (!backfillTarget) return undefined;
    return backfillTarget.backfill_context || backfillContextById[backfillTarget.id];
  }, [backfillTarget, backfillContextById]);

  useEffect(() => {
    if (!backfillTarget) {
      setBackfillPreview(null);
      setBackfillError(null);
      setBackfillResult(null);
      setBackfillRunError(null);
      setBackfillSnapshot(null);
      return;
    }

    let mounted = true;
    const loadPreview = async () => {
      setBackfillLoading(true);
      setBackfillError(null);

      try {
        const path = `/metadata/${tab}/${backfillTarget.id}/backfill/preview`;
        const swap = backfillContext;
        const response = await fetch(apiUrl(path).toString(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(authHeader ? { Authorization: authHeader } : {})
          },
          body: swap ? JSON.stringify({ swap }) : undefined
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          const message = payload?.error || payload?.message || "Failed to load backfill preview";
          if (handleAuthError(message)) return;
          throw new Error(message);
        }

        const payload = (await response.json()) as BackfillPreview;
        if (mounted) {
          setBackfillPreview(payload);
          setBackfillSnapshot(payload);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load backfill preview";
        if (handleAuthError(message)) return;
        if (mounted) {
          setBackfillError(message);
        }
      } finally {
        if (mounted) {
          setBackfillLoading(false);
        }
      }
    };

    loadPreview();
    return () => {
      mounted = false;
    };
  }, [backfillTarget, backfillContext, authHeader, tab]);

  useEffect(() => {
    if (tab === "units") {
      setSortKey("unit_name");
      setSortDirection("asc");
    } else if (tab === "weapons") {
      setSortKey("weapon_name");
      setSortDirection("asc");
    } else {
      setSortKey("airdrome_name");
      setSortDirection("asc");
    }
  }, [tab]);

  const formatRelativeTime = (value: string | null) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";

    const diffMs = date.getTime() - Date.now();
    const absSeconds = Math.abs(diffMs) / 1000;

    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
    if (absSeconds < 60) {
      return rtf.format(Math.round(diffMs / 1000), "second");
    }
    if (absSeconds < 3600) {
      return rtf.format(Math.round(diffMs / 60000), "minute");
    }
    if (absSeconds < 86400) {
      return rtf.format(Math.round(diffMs / 3600000), "hour");
    }
    if (absSeconds < 2592000) {
      return rtf.format(Math.round(diffMs / 86400000), "day");
    }
    if (absSeconds < 31536000) {
      return rtf.format(Math.round(diffMs / 2592000), "month");
    }

    return rtf.format(Math.round(diffMs / 31536000000), "year");
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  };

  const renderSortIndicator = (key: SortKey) => {
    if (sortKey !== key) return null;
    return <span className="admin-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>;
  };

  const renderSortableHeader = (label: string, key: SortKey) => (
    <th>
      <button type="button" className="admin-sort-button" onClick={() => handleSort(key)}>
        <span>{label}</span>
        {renderSortIndicator(key)}
      </button>
    </th>
  );

  const renderTableHeader = () => {
    if (tab === "units") {
      return (
        <tr>
          {renderSortableHeader("Unit", "unit_name")}
          {renderSortableHeader("Family", "family")}
          {renderSortableHeader("Domain", "domain")}
          {renderSortableHeader("Category", "category")}
          {renderSortableHeader("Role", "role")}
          {renderSortableHeader("Events", "event_count")}
          {renderSortableHeader("Updated", "updated_at")}
          <th>Actions</th>
        </tr>
      );
    }

    if (tab === "weapons") {
      return (
        <tr>
          {renderSortableHeader("Weapon", "weapon_name")}
          {renderSortableHeader("Family", "family")}
          {renderSortableHeader("Category", "category")}
          {renderSortableHeader("Guidance", "guidance")}
          {renderSortableHeader("Role", "role")}
          {renderSortableHeader("Events", "event_count")}
          {renderSortableHeader("Updated", "updated_at")}
          <th>Actions</th>
        </tr>
      );
    }

    return (
      <tr>
        {renderSortableHeader("Airdrome", "airdrome_name")}
        {renderSortableHeader("Type", "airdrome_type")}
        {renderSortableHeader("Events", "event_count")}
        {renderSortableHeader("Updated", "updated_at")}
        <th>Actions</th>
      </tr>
    );
  };

  const formatCount = (value: number) => {
    if (value >= 1_000_000) {
      const scaled = value / 1_000_000;
      return `${scaled.toFixed(scaled >= 10 ? 0 : 1)}M`;
    }
    if (value >= 1_000) {
      const scaled = value / 1_000;
      return `${scaled.toFixed(scaled >= 10 ? 0 : 1)}K`;
    }
    return value.toString();
  };

  const sortedItems = useMemo(() => {
    const normalizedItems = [...items];
    const direction = sortDirection === "asc" ? 1 : -1;

    const getValue = (item: MetadataItem): string | number => {
      if (sortKey === "event_count") return item.event_count;
      if (sortKey === "updated_at") {
        const date = item.updated_at ? new Date(item.updated_at) : null;
        return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
      }

      if (sortKey in item) {
        const value = item[sortKey as keyof MetadataItem];
        if (typeof value === "string") return value.toLowerCase();
        if (value == null) return "";
      }

      return "";
    };

    normalizedItems.sort((a, b) => {
      const left = getValue(a);
      const right = getValue(b);

      if (typeof left === "number" && typeof right === "number") {
        return (left - right) * direction;
      }

      return String(left).localeCompare(String(right), undefined, { sensitivity: "base" }) * direction;
    });

    return normalizedItems;
  }, [items, sortDirection, sortKey]);

  const updateItem = (updated: MetadataItem) => {
    setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
  };

  const handleSave = async (item: MetadataItem, updates: Record<string, string | null>) => {
    const path = `/metadata/${tab}/${item.id}`;
    const response = await fetch(apiUrl(path).toString(), {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(authHeader ? { Authorization: authHeader } : {})
      },
      body: JSON.stringify({ [tab.slice(0, -1)]: updates })
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      const message = payload?.error || payload?.message || "Failed to save changes";
      if (handleAuthError(message)) return;
      throw new Error(message);
    }

    const payload = (await response.json()) as MetadataItem & {
      backfill_required?: boolean;
      backfill_context?: MetadataItem["backfill_context"];
    };
    const backfillRequired = Boolean(payload.backfill_required) && (payload.event_count ?? item.event_count) > 0;
    const pendingContext = buildBackfillContext(item, updates, tab);
    const context = payload.backfill_context || (backfillRequired ? pendingContext : undefined);
    if (context) {
      setBackfillContextById((prev) => ({ ...prev, [item.id]: context }));
    }
    updateItem({ ...item, ...payload, backfill_required: backfillRequired, backfill_context: context });
  };

  const handleRunBackfill = async () => {
    if (!backfillTarget) return;
    setBackfillRunning(true);
    setBackfillRunError(null);

    try {
      const path = `/metadata/${tab}/${backfillTarget.id}/backfill`;
      const swap = backfillContext;
      const response = await fetch(apiUrl(path).toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(authHeader ? { Authorization: authHeader } : {})
        },
        body: swap ? JSON.stringify({ swap }) : undefined
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.error || payload?.message || "Failed to run backfill";
        if (handleAuthError(message)) return;
        throw new Error(message);
      }

      const payload = (await response.json()) as BackfillResult;
      const preview = payload.preview || backfillPreview || backfillSnapshot;
      setBackfillResult(preview ? { ...payload, preview } : payload);
      setBackfillPreview(null);

      updateItem({
        ...backfillTarget,
        backfill_required: false,
        backfill_context: undefined
      });
      setBackfillContextById((prev) => ({ ...prev, [backfillTarget.id]: undefined }));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to run backfill";
      if (handleAuthError(message)) return;
      setBackfillRunError(message);
    } finally {
      setBackfillRunning(false);
    }
  };

  const handleBackfillOpen = (item: MetadataItem) => {
    setBackfillPreview(null);
    setBackfillError(null);
    setBackfillResult(null);
    setBackfillRunError(null);

    const context = item.backfill_context || backfillContextById[item.id];
    setBackfillTarget({ ...item, backfill_context: context });
  };

  const handleExportCsv = async () => {
    setExporting(true);
    setExportError(null);

    try {
      const path = `/metadata/${tab}/export`;
      const response = await fetch(apiUrl(path).toString(), {
        headers: {
          Accept: "text/csv",
          ...(authHeader ? { Authorization: authHeader } : {})
        }
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.error || payload?.message || "Failed to export CSV";
        if (handleAuthError(message)) return;
        throw new Error(message);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("content-disposition") || "";
      const match = contentDisposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || `${tab}_metadata.csv`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to export CSV";
      if (handleAuthError(message)) return;
      setExportError(message);
    } finally {
      setExporting(false);
    }
  };

  const renderRow = (item: MetadataItem) => {
    return (
      <AdminMetadataRow
        key={item.id}
        item={item}
        tab={tab}
        onSave={handleSave}
        formatCount={formatCount}
        formatRelativeTime={formatRelativeTime}
        onBackfill={handleBackfillOpen}
      />
    );
  };

  return (
    <PageLayout route="admin" wide>
      <section className="panel panel-tight">
        <div className="admin-header">
          <div>
            <h2>Manage Metadata</h2>
            <p>Review metadata entries from the API.</p>
          </div>
          <div className="admin-header-actions">
            <button
              type="button"
              className="admin-secondary-button"
              onClick={handleExportCsv}
              disabled={exporting}
            >
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
            <a className="admin-link" href="#/admin">
              Back to Admin
            </a>
          </div>
        </div>
        <div className="admin-tabs" role="tablist" aria-label="Metadata categories">
          {tabs.map((tabItem) => (
            <button
              key={tabItem.key}
              type="button"
              role="tab"
              aria-selected={tab === tabItem.key}
              className={`admin-tab${tab === tabItem.key ? " is-active" : ""}`}
              onClick={() => setTab(tabItem.key)}
            >
              {tabItem.label}
            </button>
          ))}
        </div>
      </section>

      <section className="panel panel-list">
        {error && <p className="alert">{error}</p>}
        {exportError && <p className="alert">{exportError}</p>}
        {loading && <p className="status">Loading…</p>}
        {!loading && !error && items.length === 0 && <p className="status">No items found.</p>}
        {!error && items.length > 0 && (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>{renderTableHeader()}</thead>
              <tbody>{sortedItems.map((item) => renderRow(item))}</tbody>
            </table>
          </div>
        )}
      </section>

      {backfillTarget &&
        createPortal(
          <div className="admin-modal" role="dialog" aria-modal="true">
            <div className="admin-modal-card">
              <div className="admin-modal-header">
                <div>
                  <h3>{backfillResult ? "Backfill Summary" : "Backfill Preview"}</h3>
                  <p>
                    {backfillResult
                      ? "Review what changed after backfill."
                      : "Review impact for this metadata change."}
                  </p>
                </div>
                <button type="button" className="admin-icon-button" onClick={() => setBackfillTarget(null)}>
                  <IconX size={18} />
                </button>
              </div>
              {backfillLoading && <p className="status">Loading preview…</p>}
              {backfillError && <p className="alert">{backfillError}</p>}
              {backfillPreview && (
                <div className="admin-modal-body">
                  <div className="admin-preview-row">
                    <span>Events to backfill</span>
                    <strong>{formatCount(backfillPreview.event_count)}</strong>
                  </div>
                  {typeof backfillPreview.pilots_affected === "number" && (
                    <div className="admin-preview-row">
                      <span>Pilots affected</span>
                      <strong>{formatCount(backfillPreview.pilots_affected)}</strong>
                    </div>
                  )}
                  {backfillPreview.metrics.swap && backfillPreview.metrics.swap.length > 0 && (
                    <div className="admin-preview-section">
                      <h4>Schema deltas</h4>
                      <ul className="admin-preview-list">
                        {backfillPreview.metrics.swap.flatMap((row, index) => {
                          const items: Array<{ key: string; label: string; delta: number }> = [];
                          if (row.from_delta !== 0) {
                            items.push({
                              key: `${row.schema_name}-${row.tag_key}-${row.from_value}-${index}-from`,
                              label: `${row.schema_name} · ${row.tag_key}=${row.from_value}`,
                              delta: row.from_delta
                            });
                          }
                          if (row.to_delta !== 0) {
                            items.push({
                              key: `${row.schema_name}-${row.tag_key}-${row.to_value}-${index}-to`,
                              label: `${row.schema_name} · ${row.tag_key}=${row.to_value}`,
                              delta: row.to_delta
                            });
                          }
                          return items.map((item) => (
                            <li key={item.key}>
                              <span>{item.label}</span>
                              <strong>
                                {item.delta >= 0 ? "+" : "-"}
                                {formatCount(Math.abs(item.delta))}
                              </strong>
                            </li>
                          ));
                        })}
                      </ul>
                    </div>
                  )}
                  {backfillPreview.proficiencies.items && backfillPreview.proficiencies.items.length > 0 && (
                    <div className="admin-preview-section">
                      <h4>Proficiencies affected</h4>
                      <ul className="admin-preview-list">
                        {backfillPreview.proficiencies.items.map((proficiency) => (
                          <li key={proficiency.id}>
                            <span>{proficiency.display_name}</span>
                            <strong>{proficiency.metric}</strong>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="admin-preview-actions">
                    <button
                      type="button"
                      className="admin-primary-button"
                      onClick={handleRunBackfill}
                      disabled={backfillRunning}
                    >
                      {backfillRunning ? "Running…" : "Run backfill"}
                    </button>
                    {backfillRunError && <span className="admin-preview-error">{backfillRunError}</span>}
                  </div>
                </div>
              )}
              {backfillResult && (
                <div className="admin-modal-body">
                  <div className="admin-preview-row">
                    <span>Events backfilled</span>
                    <strong>{formatCount(backfillResult.preview?.event_count ?? 0)}</strong>
                  </div>
                  <div className="admin-preview-row">
                    <span>Pilots affected</span>
                    <strong>{formatCount(backfillResult.preview?.pilots_affected ?? 0)}</strong>
                  </div>
                  {backfillResult.preview?.metrics.swap && backfillResult.preview.metrics.swap.length > 0 && (
                    <div className="admin-preview-section">
                      <h4>Schema deltas</h4>
                      <ul className="admin-preview-list">
                        {backfillResult.preview.metrics.swap.flatMap((row, index) => {
                          const items: Array<{ key: string; label: string; delta: number }> = [];
                          if (row.from_delta !== 0) {
                            items.push({
                              key: `${row.schema_name}-${row.tag_key}-${row.from_value}-${index}-from`,
                              label: `${row.schema_name} · ${row.tag_key}=${row.from_value}`,
                              delta: row.from_delta
                            });
                          }
                          if (row.to_delta !== 0) {
                            items.push({
                              key: `${row.schema_name}-${row.tag_key}-${row.to_value}-${index}-to`,
                              label: `${row.schema_name} · ${row.tag_key}=${row.to_value}`,
                              delta: row.to_delta
                            });
                          }
                          return items.map((item) => (
                            <li key={item.key}>
                              <span>{item.label}</span>
                              <strong>
                                {item.delta >= 0 ? "+" : "-"}
                                {formatCount(Math.abs(item.delta))}
                              </strong>
                            </li>
                          ));
                        })}
                      </ul>
                    </div>
                  )}
                  {backfillResult.preview?.proficiencies.items && backfillResult.preview.proficiencies.items.length > 0 && (
                    <div className="admin-preview-section">
                      <h4>Proficiencies affected</h4>
                      <ul className="admin-preview-list">
                        {backfillResult.preview.proficiencies.items.map((proficiency) => (
                          <li key={proficiency.id}>
                            <span>{proficiency.display_name}</span>
                            <strong>{proficiency.metric}</strong>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="admin-preview-actions">
                    <button type="button" className="admin-secondary-button" onClick={() => setBackfillTarget(null)}>
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </PageLayout>
  );
};

type AdminMetadataRowProps = {
  item: MetadataItem;
  tab: MetadataTab;
  onSave: (item: MetadataItem, updates: Record<string, string | null>) => Promise<void>;
  formatCount: (value: number) => string;
  formatRelativeTime: (value: string | null) => string;
  onBackfill: (item: MetadataItem) => void;
};

const AdminMetadataRow = ({
  item,
  tab,
  onSave,
  formatCount,
  formatRelativeTime,
  onBackfill
}: AdminMetadataRowProps) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (!editing) {
      if (tab === "units" && "unit_name" in item) {
        setDraft({
          family: item.family ?? "",
          domain: item.domain ?? "",
          category: item.category ?? "",
          role: item.role ?? ""
        });
      } else if (tab === "weapons" && "weapon_name" in item) {
        setDraft({
          family: item.family ?? "",
          category: item.category ?? "",
          guidance: item.guidance ?? "",
          role: item.role ?? ""
        });
      } else if (tab === "airdromes" && "airdrome_name" in item) {
        setDraft({
          airdrome_type: item.airdrome_type ?? ""
        });
      }
    }
  }, [editing, item, tab]);

  const handleChange = (key: string, value: string) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(item, draft);
      setEditing(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setError(null);
  };

  const renderCount = () => {
    const label = formatCount(item.event_count);
    if (!item.backfill_required || item.event_count === 0) {
      return <span className="admin-count">{label}</span>;
    }
    return (
      <button
        type="button"
        className="admin-count admin-count-alert"
        title="Backfill required"
        onClick={() => onBackfill(item)}
      >
        {label}
      </button>
    );
  };

  const renderActions = () => {
    if (editing) {
      return (
        <div className="admin-row-actions">
          <button type="button" className="admin-icon-button" onClick={handleSave} disabled={saving}>
            <IconCheck size={18} />
          </button>
          <button type="button" className="admin-icon-button" onClick={handleCancel} disabled={saving}>
            <IconX size={18} />
          </button>
        </div>
      );
    }

    return (
      <div className="admin-row-actions">
        <button type="button" className="admin-icon-button" onClick={() => setEditing(true)}>
          <IconPencil size={18} />
        </button>
      </div>
    );
  };

  const missingTranslationTitle = "Missing translations for en-us";

  const isMissingTranslation = (value: string, key: string) => {
    const trimmed = value.trim();
    if (!trimmed) return false;
    return !t(`${key}.${trimmed}`, "");
  };

  const renderMetadataCell = (value: string | null | undefined, key: string) => {
    if (!value || !value.trim()) {
      return "—";
    }

    const missing = isMissingTranslation(value, key);
    return (
      <span className={missing ? "admin-translation-missing" : undefined} title={missing ? missingTranslationTitle : undefined}>
        {value}
      </span>
    );
  };

  if (tab === "units" && "unit_name" in item) {
    return (
      <tr>
        <td>{item.unit_name}</td>
        <td>
          {editing ? (
            <input
              className="admin-input"
              value={draft.family ?? ""}
              onChange={(event) => handleChange("family", event.target.value)}
            />
          ) : (
            item.family || "—"
          )}
        </td>
        <td>
          {editing ? (
            <input
              className="admin-input"
              value={draft.domain ?? ""}
              onChange={(event) => handleChange("domain", event.target.value)}
            />
          ) : (
            renderMetadataCell(item.domain, "dcs.units.domains")
          )}
        </td>
        <td>
          {editing ? (
            <input
              className="admin-input"
              value={draft.category ?? ""}
              onChange={(event) => handleChange("category", event.target.value)}
            />
          ) : (
            renderMetadataCell(item.category, "dcs.units.categories")
          )}
        </td>
        <td>
          {editing ? (
            <input
              className="admin-input"
              value={draft.role ?? ""}
              onChange={(event) => handleChange("role", event.target.value)}
            />
          ) : (
            renderMetadataCell(item.role, "dcs.units.roles")
          )}
        </td>
        <td>{renderCount()}</td>
        <td>{formatRelativeTime(item.updated_at)}</td>
        <td>
          {renderActions()}
          {error && <div className="admin-row-error">{error}</div>}
        </td>
      </tr>
    );
  }

  if (tab === "weapons" && "weapon_name" in item) {
    return (
      <tr>
        <td>{item.weapon_name}</td>
        <td>
          {editing ? (
            <input
              className="admin-input"
              value={draft.family ?? ""}
              onChange={(event) => handleChange("family", event.target.value)}
            />
          ) : (
            item.family || "—"
          )}
        </td>
        <td>
          {editing ? (
            <input
              className="admin-input"
              value={draft.category ?? ""}
              onChange={(event) => handleChange("category", event.target.value)}
            />
          ) : (
            renderMetadataCell(item.category, "dcs.weapons.categories")
          )}
        </td>
        <td>
          {editing ? (
            <input
              className="admin-input"
              value={draft.guidance ?? ""}
              onChange={(event) => handleChange("guidance", event.target.value)}
            />
          ) : (
            renderMetadataCell(item.guidance, "dcs.weapons.guidance")
          )}
        </td>
        <td>
          {editing ? (
            <input
              className="admin-input"
              value={draft.role ?? ""}
              onChange={(event) => handleChange("role", event.target.value)}
            />
          ) : (
            renderMetadataCell(item.role, "dcs.weapons.roles")
          )}
        </td>
        <td>{renderCount()}</td>
        <td>{formatRelativeTime(item.updated_at)}</td>
        <td>
          {renderActions()}
          {error && <div className="admin-row-error">{error}</div>}
        </td>
      </tr>
    );
  }

  if (tab === "airdromes" && "airdrome_name" in item) {
    return (
      <tr>
        <td>{item.airdrome_name}</td>
        <td>
          {editing ? (
            <input
              className="admin-input"
              value={draft.airdrome_type ?? ""}
              onChange={(event) => handleChange("airdrome_type", event.target.value)}
            />
          ) : (
            renderMetadataCell(item.airdrome_type, "dcs.airdromes.types")
          )}
        </td>
        <td>{renderCount()}</td>
        <td>{formatRelativeTime(item.updated_at)}</td>
        <td>
          {renderActions()}
          {error && <div className="admin-row-error">{error}</div>}
        </td>
      </tr>
    );
  }

  return null;
};

const buildBackfillContext = (
  item: MetadataItem,
  updates: Record<string, string | null>,
  tab: MetadataTab
) => {
  const getValue = (value?: string | null) => (value ?? "").toString();
  const mappings: Record<string, string[]> = {};

  if (tab === "units") {
    mappings.family = ["unit_family", "killer_unit_family", "victim_unit_family"];
    mappings.domain = ["unit_domain", "killer_unit_domain", "victim_unit_domain"];
    mappings.category = ["unit_category", "killer_unit_category", "victim_unit_category"];
    mappings.role = ["unit_role", "killer_unit_role", "victim_unit_role"];
  }

  if (tab === "weapons") {
    mappings.family = ["weapon_family"];
    mappings.category = ["weapon_category"];
    mappings.guidance = ["weapon_guidance"];
    mappings.role = ["weapon_role"];
  }

  const entry = Object.entries(updates).find(([key, value]) => {
    const itemValue = item[key as keyof typeof item];
    const previous = getValue(
      typeof itemValue === "string" || itemValue == null
        ? (itemValue as string | null)
        : itemValue?.toString?.() ?? null
    );
    return getValue(value) !== previous;
  });

  if (!entry) return undefined;
  const [field, newValue] = entry;
  const tagKeys = mappings[field];
  if (!tagKeys || tagKeys.length === 0) return undefined;

  const itemFieldValue = item[field as keyof typeof item];
  const fromValue = getValue(
    typeof itemFieldValue === "string" || itemFieldValue == null
      ? (itemFieldValue as string | null)
      : itemFieldValue?.toString?.() ?? null
  );
  const toValue = getValue(newValue);
  if (fromValue === toValue) return undefined;
  if (!fromValue && !toValue) return undefined;

  return {
    tag_keys: tagKeys,
    from: fromValue,
    to: toValue
  };
};

export default AdminMetadataPage;
